import { useState, useEffect, useRef } from 'react'
import { Camera, Search, Loader2, Package, Info, X, ImageOff, ZoomIn, Filter, Leaf, Sun, Share2, Check, Link2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import BarcodeScanner from '../components/BarcodeScanner'

function Home({ user }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [allResults, setAllResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [error, setError] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [enlargedImage, setEnlargedImage] = useState(null)
  const [activeFilters, setActiveFilters] = useState([])
  const [availableFilters, setAvailableFilters] = useState([])
  const searchInputRef = useRef(null)

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (activeFilters.length === 0) {
      setSearchResults(allResults)
    } else {
      const filtered = allResults.filter(product => {
        const typeMatch = !activeFilters.includes(product.cigarette_type)
        const featureMatch = !activeFilters.includes(product.cigarette_feature)
        return !typeMatch || !featureMatch
      })
      setSearchResults(filtered)
    }
  }, [activeFilters, allResults])

  const extractFilters = (results) => {
    const types = new Set()
    const features = new Set()
    
    results.forEach(product => {
      if (product.cigarette_type) types.add(product.cigarette_type)
      if (product.cigarette_feature) features.add(product.cigarette_feature)
    })
    
    return [...types, ...features].filter(Boolean)
  }

  const getLocalImagePath = (imageLocalPath) => {
    if (!imageLocalPath) return null
    const filename = imageLocalPath.split(/[/\\]/).pop()
    return `/images/${filename}`
  }

  const searchProduct = async (query) => {
    if (!query.trim()) {
      setError('请输入商品名称或条码')
      return
    }

    setLoading(true)
    setError(null)
    setSearchResults([])
    setAllResults([])
    setSelectedProduct(null)
    setActiveFilters([])

    try {
      const trimmedQuery = query.trim()
      const isNumeric = /^\d+$/.test(trimmedQuery)
      const keywords = trimmedQuery.split(/\s+/)
      
      let queryBuilder = supabase
        .from('tobacco_products')
        .select('*')

      if (isNumeric) {
        queryBuilder = queryBuilder.or(`small_box_barcode.eq.${trimmedQuery},carton_barcode.eq.${trimmedQuery},name.ilike.%${trimmedQuery}%`)
      } else if (keywords.length === 1) {
        queryBuilder = queryBuilder.ilike('name', `%${keywords[0]}%`)
      } else {
        queryBuilder = queryBuilder.ilike('name', `%${keywords[0]}%`)
      }

      const { data, error: searchError } = await queryBuilder.limit(200)

      if (searchError) throw searchError

      let filteredData = data || []
      
      if (!isNumeric && keywords.length > 1) {
        filteredData = filteredData.filter(product => {
          return keywords.every(kw => 
            product.name && product.name.toLowerCase().includes(kw.toLowerCase())
          )
        })
      }

      if (filteredData.length > 0) {
        setAllResults(filteredData)
        setSearchResults(filteredData)
        setAvailableFilters(extractFilters(filteredData))
      } else {
        setError('未找到匹配的烟草信息')
      }
    } catch (err) {
      console.error('查询错误:', err)
      setError(`查询失败: ${err.message}`)
    } finally {
      setLoading(false)
      setSearchQuery('')
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }, 100)
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    searchProduct(searchQuery)
  }

  const handleBarcodeScan = (barcode) => {
    setScannerOpen(false)
    setSearchQuery(barcode)
    searchProduct(barcode)
  }

  const toggleFilter = (filter) => {
    setActiveFilters(prev => {
      if (prev.includes(filter)) {
        return prev.filter(f => f !== filter)
      } else {
        return [...prev, filter]
      }
    })
  }

  const ProductCard = ({ product, onClick }) => {
    const [imgError, setImgError] = useState(false)
    const localImagePath = getLocalImagePath(product.image_local_path)
    const hasExtraInfo = product.cigarette_type || product.cigarette_feature
    
    return (
      <div
        className="group bg-white/80 backdrop-blur-sm border border-green-200/60 rounded-3xl p-5 cursor-pointer transition-all duration-500 hover:scale-[1.02] hover:rotate-[0.5deg] hover:shadow-xl hover:shadow-green-200/50 hover:border-green-300/80 hover:bg-white/95"
        onClick={onClick}
      >
        <div className="flex gap-5">
          <div 
            className="w-28 h-28 flex-shrink-0 bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl overflow-hidden flex items-center justify-center relative"
            onClick={(e) => {
              e.stopPropagation()
              if (localImagePath && !imgError) {
                setEnlargedImage(localImagePath)
              }
            }}
          >
            {localImagePath && !imgError ? (
              <>
                <img 
                  src={localImagePath} 
                  alt={product.name}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
                  onError={() => setImgError(true)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-2">
                  <ZoomIn className="w-5 h-5 text-white drop-shadow-lg" />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center text-green-300">
                <ImageOff className="w-8 h-8" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-3 mb-3">
              <h3 className="font-bold text-green-800 text-lg leading-tight">{product.name}</h3>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-xs font-medium border border-green-200/50">
                  {product.product_type}
                </span>
                {hasExtraInfo && (
                  <div className="flex flex-wrap gap-1 justify-end">
                    {product.cigarette_type && (
                      <span className="px-2.5 py-0.5 bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 rounded-full text-xs font-medium border border-amber-200/50">
                        {product.cigarette_type}
                      </span>
                    )}
                    {product.cigarette_feature && (
                      <span className="px-2.5 py-0.5 bg-gradient-to-r from-sky-50 to-blue-50 text-sky-700 rounded-full text-xs font-medium border border-sky-200/50">
                        {product.cigarette_feature}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-1.5 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <span className="text-green-500">•</span>
                <span className="font-medium text-gray-500">条码:</span>
                <span className="font-mono text-gray-700">{product.small_box_barcode || '-'}</span>
              </p>
              <p className="flex items-center gap-2">
                <span className="text-amber-500">•</span>
                <span className="font-medium text-gray-500">价格:</span>
                <span className="text-green-700 font-medium">{product.small_box_price || '-'}</span>
              </p>
              <p className="flex items-center gap-2">
                <span className="text-sky-500">•</span>
                <span className="font-medium text-gray-500">焦油:</span>
                <span>{product.tar_content ? `${product.tar_content}mg` : '-'}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const ProductDetail = ({ product, onClose }) => {
    const [imgError, setImgError] = useState(false)
    const [shareStatus, setShareStatus] = useState(null)
    const localImagePath = getLocalImagePath(product.image_local_path)

    const handleShare = async () => {
      const shareUrl = `${window.location.origin}/share/${product.id}`
      const shareTitle = `${product.name} - 烟草信息查询`
      const shareText = `${product.name}，${product.product_type || ''}，焦油${product.tar_content || '-'}mg，小盒价格${product.small_box_price || '-'}`

      if (navigator.share && navigator.canShare) {
        try {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            url: shareUrl,
          })
          setShareStatus('shared')
          setTimeout(() => setShareStatus(null), 2000)
        } catch (err) {
          if (err.name !== 'AbortError') {
            copyToClipboard(shareUrl)
          }
        }
      } else {
        copyToClipboard(shareUrl)
      }
    }

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        setShareStatus('copied')
        setTimeout(() => setShareStatus(null), 2000)
      } catch (err) {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          setShareStatus('copied')
          setTimeout(() => setShareStatus(null), 2000)
        } catch (e) {
          console.error('复制失败:', e)
        }
        document.body.removeChild(textArea)
      }
    }
    
    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-gradient-to-br from-white via-green-50/30 to-amber-50/20 backdrop-blur-xl border border-green-200/60 max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl shadow-green-200/30 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100/80 rounded-full transition-all duration-300 z-10"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-8">
            <div className="flex gap-8 mb-8">
              <div 
                className="w-44 h-44 flex-shrink-0 bg-gradient-to-br from-green-100 to-amber-100 rounded-3xl overflow-hidden flex items-center justify-center relative group cursor-pointer shadow-lg shadow-green-200/30"
                onClick={() => {
                  if (localImagePath && !imgError) {
                    setEnlargedImage(localImagePath)
                  }
                }}
              >
                {localImagePath && !imgError ? (
                  <>
                    <img 
                      src={localImagePath} 
                      alt={product.name}
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
                      onError={() => setImgError(true)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-3">
                      <div className="flex items-center gap-2 text-white text-sm font-medium">
                        <ZoomIn className="w-4 h-4" />
                        <span>点击放大</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center text-green-300">
                    <ImageOff className="w-12 h-12" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-green-800 mb-4 leading-tight">{product.name}</h2>
                <div className="flex flex-wrap gap-2">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-medium border border-green-200/50">
                    {product.product_type || '未知类型'}
                  </span>
                  {product.cigarette_type && (
                    <span className="px-4 py-1.5 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 rounded-full text-sm font-medium border border-amber-200/50">
                      {product.cigarette_type}
                    </span>
                  )}
                  {product.cigarette_feature && (
                    <span className="px-4 py-1.5 bg-gradient-to-r from-sky-100 to-blue-100 text-sky-700 rounded-full text-sm font-medium border border-sky-200/50">
                      {product.cigarette_feature}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <InfoItem label="焦油含量" value={product.tar_content ? `${product.tar_content} mg` : '-'} color="green" />
              <InfoItem label="烟碱含量" value={product.nicotine_content ? `${product.nicotine_content} mg` : '-'} color="amber" />
              <InfoItem label="一氧化碳" value={product.carbon_monoxide_content ? `${product.carbon_monoxide_content} mg` : '-'} color="sky" />
              <InfoItem label="小盒条码" value={product.small_box_barcode || '-'} color="green" mono />
              <InfoItem label="条盒条码" value={product.carton_barcode || '-'} color="green" mono />
              <InfoItem label="小盒价格" value={product.small_box_price || '-'} color="amber" highlight />
              <InfoItem label="条盒价格" value={product.carton_price || '-'} color="amber" />
              <InfoItem label="批发价格" value={product.wholesale_price || '-'} color="amber" />
              <InfoItem label="上市时间" value={product.launch_time || '-'} color="sky" />
              <InfoItem label="包装形式" value={product.packaging_form || '-'} color="green" span2 />
              <InfoItem label="烟支规格" value={product.cigarette_spec || '-'} color="sky" />
              <InfoItem label="烟支长度" value={product.cigarette_length || '-'} color="sky" />
            </div>

            <div className="mt-6 pt-6 border-t border-green-200/50">
              <button
                onClick={handleShare}
                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold transition-all duration-300 ${
                  shareStatus === 'copied' 
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg shadow-green-300/40'
                    : shareStatus === 'shared'
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg shadow-green-300/40'
                    : 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white shadow-lg shadow-blue-300/40 hover:shadow-xl hover:scale-[1.02]'
                }`}
              >
                {shareStatus === 'copied' ? (
                  <>
                    <Check className="w-5 h-5" />
                    <span>链接已复制</span>
                  </>
                ) : shareStatus === 'shared' ? (
                  <>
                    <Check className="w-5 h-5" />
                    <span>分享成功</span>
                  </>
                ) : (
                  <>
                    <Share2 className="w-5 h-5" />
                    <span>分享到微信</span>
                  </>
                )}
              </button>
              <p className="text-center text-blue-600 text-sm mt-3 flex items-center justify-center gap-1.5 font-medium">
                <Link2 className="w-4 h-4" />
                点击复制分享链接，粘贴发送给好友
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const InfoItem = ({ label, value, color = 'green', mono, highlight, span2 }) => {
    const colorClasses = {
      green: 'from-green-50 to-emerald-50 border-green-200/50',
      amber: 'from-amber-50 to-yellow-50 border-amber-200/50',
      sky: 'from-sky-50 to-blue-50 border-sky-200/50',
    }
    
    return (
      <div className={`p-3.5 bg-gradient-to-br ${colorClasses[color]} border rounded-2xl ${span2 ? 'col-span-2' : ''}`}>
        <p className="text-xs text-gray-500 mb-1 font-medium">{label}</p>
        <p className={`font-semibold text-gray-800 ${mono ? 'font-mono text-sm' : ''} ${highlight ? 'text-green-700' : ''}`}>{value}</p>
      </div>
    )
  }

  const EnlargedImageModal = ({ src, onClose }) => (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-lg z-[60] flex items-center justify-center p-6"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300"
      >
        <X className="w-6 h-6" />
      </button>
      <img 
        src={src} 
        alt="放大图片"
        className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-5 shadow-lg shadow-green-300/40 animate-float">
            <Leaf className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-green-800 mb-4">
            烟草信息查询
          </h1>
          <p className="text-gray-600 text-lg max-w-md mx-auto">
            通过扫描条码或输入名称查询烟草产品信息
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm border-2 border-green-300/70 rounded-3xl p-6 sm:p-8 mb-8 shadow-xl shadow-green-200/40">
          <form onSubmit={handleSearchSubmit}>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-green-500 w-6 h-6" />
                <input
                  type="text"
                  id="searchQuery"
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="请输入烟草名称或条码（多关键词用空格分隔）"
                  className="w-full bg-white border-2 border-green-300 rounded-full py-4 pl-14 pr-5 text-gray-800 text-lg placeholder:text-green-400/80 focus:outline-none focus:ring-4 focus:ring-green-300/40 focus:border-green-500 focus:bg-white transition-all duration-300 hover:border-green-400 shadow-inner"
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !searchQuery.trim()}
                className="bg-gradient-to-r from-green-400 to-emerald-500 text-white font-semibold rounded-full px-8 py-4 flex items-center justify-center gap-2 shadow-lg shadow-green-300/40 transition-all duration-300 hover:shadow-xl hover:shadow-green-300/50 hover:scale-105 hover:from-green-500 hover:to-emerald-600 disabled:opacity-50 disabled:hover:scale-100"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
                <span>查询</span>
              </button>
              <button
                type="button"
                onClick={() => setScannerOpen(true)}
                disabled={loading}
                className="bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-900 font-semibold rounded-full px-8 py-4 flex items-center justify-center gap-2 shadow-lg shadow-amber-300/40 transition-all duration-300 hover:shadow-xl hover:shadow-amber-300/50 hover:scale-105 hover:from-amber-500 hover:to-yellow-600 disabled:opacity-50 disabled:hover:scale-100"
              >
                <Camera className="h-5 w-5" />
                <span>扫描条码</span>
              </button>
            </div>
          </form>
        </div>

        {error && (
          <div className="mb-8 p-5 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200/60 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full">
                <Info className="h-5 w-5 text-red-500" />
              </div>
              <span className="text-red-700 font-medium">{error}</span>
            </div>
          </div>
        )}

        {availableFilters.length > 0 && (
          <div className="mb-8 p-5 bg-white/60 backdrop-blur-sm border border-green-200/60 rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Filter className="w-5 h-5 text-green-600" />
              <span className="font-medium text-gray-700">筛选标签</span>
              {activeFilters.length > 0 && (
                <button
                  onClick={() => setActiveFilters([])}
                  className="ml-auto text-sm text-green-600 hover:text-green-700 font-medium hover:underline"
                >
                  清除筛选
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {availableFilters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => toggleFilter(filter)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeFilters.includes(filter)
                      ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-md shadow-green-300/40 scale-105'
                      : 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200/50 hover:border-green-300 hover:shadow-md'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-5">
              <h2 className="text-xl font-bold text-green-800">
                查询结果
              </h2>
              <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-medium">
                {searchResults.length} 条
              </span>
              {activeFilters.length > 0 && (
                <span className="text-sm text-gray-500">
                  （已筛选）
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {searchResults.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={() => setSelectedProduct(product)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-green-50 via-emerald-50/50 to-amber-50/30 border border-green-200/60 rounded-3xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200/30 to-transparent rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-amber-200/30 to-transparent rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-400 to-emerald-500 rounded-full">
                <Sun className="w-5 h-5 text-white" />
              </div>
              <h4 className="text-lg font-bold text-green-800">使用说明</h4>
            </div>
            <ul className="space-y-3">
              {[
                '在搜索框中输入烟草名称或条码，点击"查询"按钮获取产品信息',
                '支持多关键词搜索，用空格分隔（如"中华 细"会搜索名称同时包含"中华"和"细"的产品）',
                '点击"扫描条码"按钮，使用摄像头扫描产品条码',
                '查询结果可点击标签（如"中支"、"细支"、"爆珠"）进行筛选',
                '点击产品卡片查看详细信息，点击图片可放大查看',
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <span className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-green-100 to-emerald-100 text-green-600 rounded-full text-sm font-medium flex-shrink-0 mt-0.5">
                    {index + 1}
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {scannerOpen && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {enlargedImage && (
        <EnlargedImageModal
          src={enlargedImage}
          onClose={() => setEnlargedImage(null)}
        />
      )}
    </div>
  )
}

export default Home
